import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiFolder, FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { Button, Card, EmptyState, Input, Modal, Spinner, Textarea } from "../ui";
import { getProjects, createProject, updateProject, deleteProject } from "../../api/communityContent";
import { useToast } from "../../context/ToastContext";
import { useConfirm } from "../../context/ConfirmContext";
import { formatSmartTimestamp } from "../../utils/format";

export default function ProjectsTab({ communityId, isAdmin, currentUserId }) {
  const [searchParams] = useSearchParams();
  const highlightProjectId = searchParams.get("project");
  const highlightRef = useRef(null);
  const { notify } = useToast();
  const confirm = useConfirm();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectModal, setProjectModal] = useState(null);
  const [projectForm, setProjectForm] = useState({ title: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProjects(communityId)
      .then((res) => setProjects(res.data?.projects || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [communityId]);

  useEffect(() => {
    if (!loading && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, highlightProjectId]);

  const openModal = (p) => {
    setProjectModal(p || "new");
    setProjectForm(p ? { title: p.title, description: p.description || "" } : { title: "", description: "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (projectModal === "new") {
        const r = await createProject(communityId, projectForm);
        setProjects((prev) => [...prev, r.data.project]);
      } else {
        const r = await updateProject(communityId, projectModal.id, projectForm);
        setProjects((prev) => prev.map((p) => (p.id === projectModal.id ? r.data.project : p)));
      }
      setProjectModal(null);
      notify("Project saved.", "success");
    } catch {
      notify("Couldn't save project.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pId) => {
    const ok = await confirm({ title: "Delete this project?", description: "This project will be permanently removed for everyone in the community. This cannot be undone." });
    if (!ok) return;
    try {
      await deleteProject(communityId, pId);
      setProjects((prev) => prev.filter((p) => p.id !== pId));
      notify("Project deleted.", "success");
    } catch {
      notify("Couldn't delete project.", "error");
    }
  };

  if (loading) {
    return (
      <Card className="flex justify-center py-12">
        <Spinner />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => openModal(null)}>
          <FiPlus className="h-4 w-4" /> Add project
        </Button>
      </div>
      {projects.length === 0 ? (
        <EmptyState
          icon={<FiFolder className="h-6 w-6" />}
          title="No projects yet"
          description="Be the first to share a project with this community."
          action={<Button onClick={() => openModal(null)}><FiPlus className="h-4 w-4" /> Add project</Button>}
        />
      ) : (
        projects.map((p) => {
          const isHighlighted = String(p.id) === highlightProjectId;
          return (
            <Card key={p.id} ref={isHighlighted ? highlightRef : undefined} className={`p-4 ${isHighlighted ? "ring-2 ring-primary-400" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{p.title}</p>
                  <p className="mt-1 text-sm text-slate-500">{p.description}</p>
                  <p className="mt-2 text-xs text-slate-400">
                    Created by{" "}
                    {p.creator?.id ? (
                      <Link to={`/profile/${p.creator.id}`} className="font-medium text-primary-600 hover:underline">
                        {p.creator.name} {p.creator.lastName}
                      </Link>
                    ) : (
                      "a member"
                    )}
                    {" · "}
                    {formatSmartTimestamp(p.createdAt)}
                  </p>
                </div>
                {(isAdmin || p.createdBy === currentUserId) && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => openModal(p)} className="text-slate-400 hover:text-primary-600">
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-red-500">
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </Card>
          );
        })
      )}

      <Modal
        open={Boolean(projectModal)}
        onClose={() => setProjectModal(null)}
        title={projectModal === "new" ? "Add project" : "Edit project"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setProjectModal(null)}>Cancel</Button>
            <Button onClick={save} loading={saving}>Save project</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Project title" value={projectForm.title} onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })} />
          <Textarea label="Description" rows={3} value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
